/**
 * What a Data Risk page would show if this build published it — named, not mocked.
 *
 * This panel is **part of the page**, not an apology at the bottom of it, and the
 * reason is the one `PRODUCT.md` §4 gives about states: a security lead who does not
 * know that USB events have no read path will read a quiet page as *no USB events
 * happened*. Those are different facts and the difference is the whole job.
 *
 * Each row names the surface, the table it would come from, and what it would have
 * shown. Nothing here is a control, and specifically **not a disabled one** — a
 * greyed button asserts "this could do something, but not for you", and here nothing
 * could, for anybody, in this build. The same decision `PosturePanel` made on
 * `/policies` about `wp_policies`.
 *
 * The four entries, and what was actually checked before each was written down:
 *
 *  1. **USB events.** `workforce.view_usb_events` is in the permission enum and is
 *     half of this route's menu requirement; `API_CONTRACT.md` §7.3 says it has **no
 *     v1 endpoint of its own** and gates sensitive data at chain step 6 instead. The
 *     only USB signal a browser can obtain is a `usb_unapproved` **alert**.
 *  2. **File operations.** Identically `workforce.view_file_events`, no v1 endpoint;
 *     the only signal is a `bulk_file_operation` alert.
 *  3. **`wp_daily_aggregates.blocked_seconds` per employee.** `DATA_MODEL.md` names
 *     `wp_daily_blocked_idx` and says in as many words that the index exists *"cho
 *     trang Data Risk"* — and no endpoint selects the column. It is not in
 *     `DailyAggregateRow`, not in `WebsiteUsageRow`, not anywhere on the wire.
 *  4. **Blacklisted visits per employee.** `wp_web_usage_blacklist_idx` is likewise
 *     documented as this page's index and likewise has no endpoint.
 *     `GET /activities/websites` rolls up by `(domain × classification)` and carries
 *     an `employee_count`, not an employee.
 *
 * A fifth thing is stated with them because it changes what the page can ask rather
 * than what it can show: **`Incident` publishes no category**, though
 * `auto-create.ts` classifies `bulk_file_operation` as `data_risk`. So incidents
 * cannot be narrowed to data-risk ones from a browser, and this page reaches them
 * only through an alert's own `incident_id`.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';

export function AbsentSurfaces(): ReactNode {
  const { t } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="l5-absent-heading" data-testid="l5-absent">
      <h2 className="wp-section__title" id="l5-absent-heading">
        {t('dataRisk.absent.title')}
      </h2>
      <p>{t('dataRisk.absent.body')}</p>
      <ul className="wp-list">
        <li data-testid="l5-absent-usb">{t('dataRisk.absent.usb')}</li>
        <li data-testid="l5-absent-file">{t('dataRisk.absent.file')}</li>
        <li data-testid="l5-absent-blocked">{t('dataRisk.absent.blockedSeconds')}</li>
        <li data-testid="l5-absent-visits">{t('dataRisk.absent.visits')}</li>
        <li data-testid="l5-absent-category">{t('dataRisk.absent.category')}</li>
      </ul>
      <p className="wp-small wp-muted" data-testid="l5-absent-nocontrols">
        {t('dataRisk.absent.noControls')}
      </p>
    </section>
  );
}
